import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
// @ts-ignore
import { useProperty, useCollectionBinding } from '@vibesites/runtime/react';
// @ts-ignore
import type { NeighborhoodPOI } from '@vibesites/schema';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faUtensils,
  faBagShopping,
  faGraduationCap,
  faFilm,
  faHeartPulse,
  faTree,
  faBus,
  faBriefcase,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

// ── Category definitions ────────────────────────────────────────────────

const CATEGORIES: { key: string; label: string; shortLabel: string; icon: IconDefinition }[] = [
  { key: 'Restaurants', label: 'Restaurants', shortLabel: 'Dine', icon: faUtensils },
  { key: 'Shopping & Retail', label: 'Shopping & Retail', shortLabel: 'Shop', icon: faBagShopping },
  { key: 'Education', label: 'Education', shortLabel: 'Edu', icon: faGraduationCap },
  { key: 'Entertainment', label: 'Entertainment', shortLabel: 'Fun', icon: faFilm },
  { key: 'Health and Wellness', label: 'Health & Wellness', shortLabel: 'Health', icon: faHeartPulse },
  { key: 'Nature & Outdoor Attractions', label: 'Nature & Outdoors', shortLabel: 'Nature', icon: faTree },
  { key: 'Transportation & Transit', label: 'Transit', shortLabel: 'Transit', icon: faBus },
  { key: 'Major Employers & Offices', label: 'Employers & Offices', shortLabel: 'Work', icon: faBriefcase },
];

// ── Google Maps script loader (singleton) ───────────────────────────────

const API_KEY = (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoadPromise: Promise<void> | null = null;

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (mapsLoadPromise) return mapsLoadPromise;

  mapsLoadPromise = new Promise<void>((resolve, reject) => {
    const w = window as any;
    if (w.google?.maps?.Map) {
      resolve();
      return;
    }

    // Another loader may already have appended the script
    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existing) {
      const poll = () => (w.google?.maps?.Map ? resolve() : setTimeout(poll, 100));
      poll();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      mapsLoadPromise = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });

  return mapsLoadPromise;
}

// ── SVG marker icon builder ─────────────────────────────────────────────

function buildMarkerSvg(iconDef: IconDefinition, bg = '#222'): string {
  const [w, h, , , raw] = iconDef.icon;
  const d = Array.isArray(raw) ? raw[0] : raw;
  const size = 32;
  const area = 16;
  const sc = Math.min(area / w, area / h);
  const tx = (size - w * sc) / 2;
  const ty = (size - h * sc) / 2;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
      `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="${bg}" stroke="white" stroke-width="2"/>` +
      `<g transform="translate(${tx},${ty}) scale(${sc})"><path fill="white" d="${d}"/></g>` +
      `</svg>`,
  )}`;
}

// ── InfoWindow content builder ──────────────────────────────────────────

/** Treat "false", "null", "undefined", and empty/whitespace as absent. */
function safeStr(v: unknown): string {
  if (typeof v !== 'string') return '';
  const t = v.trim();
  if (!t || t === 'false' || t === 'null' || t === 'undefined') return '';
  return t;
}

function parseCoordinate(v: unknown): number | null {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  const t = String(v).trim();
  if (!t || t === 'false' || t === 'null' || t === 'undefined') return null;
  const coord = Number(t);
  return Number.isFinite(coord) ? coord : null;
}

function formatDistance(d: string | null | undefined): string | null {
  const t = safeStr(d);
  if (!t) return null;
  return t.toLowerCase().endsWith('mi') ? t : `${t} mi`;
}

function buildInfoContent(poi: NeighborhoodPOI): string {
  const name = safeStr(poi.poi_name);
  const dist = formatDistance(poi.poi_miles_from_property);
  const body = safeStr(poi.poi_short_description) || safeStr(poi.poi_address);
  const directionsUrl = safeStr(poi.poi_directions_map_url);
  const websiteUrl = safeStr(poi.poi_business_url);
  const hasActions = directionsUrl || websiteUrl;

  // Override Google Maps InfoWindow internal styles (scroll gutter, borders, max-width)
  const iwOverrides =
    `<style>` +
    `.gm-style-iw-d{overflow:hidden!important;padding:0!important}` +
    `.gm-style-iw{padding:14px 16px!important;max-width:calc(100vw - 60px)!important}` +
    `.gm-style-iw-tc::after,.gm-style-iw-d>div>div{border:none!important}` +
    `.gm-ui-hover-effect{top:4px!important;right:4px!important}` +
    `</style>`;

  const btnStyle =
    'display:inline-flex;align-items:center;justify-content:center;' +
    'padding:7px 12px;border:1.5px solid var(--prop-primary,#4a7c8a);' +
    'color:var(--prop-primary,#4a7c8a);text-decoration:none;font-size:11px;' +
    'font-weight:600;letter-spacing:.05em;text-transform:uppercase;' +
    'border-radius:2px;text-align:center;white-space:nowrap;min-width:0;flex:1 1 auto';

  let html =
    iwOverrides +
    `<div style="font-family:var(--vibe-font-body,sans-serif);color:#222;max-width:280px;box-sizing:border-box">` +
    `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px">` +
    `<strong style="font-size:15px;line-height:1.3;min-width:0;word-wrap:break-word">${name}</strong>`;

  if (dist) {
    html +=
      `<span style="background:var(--prop-primary,#4a7c8a);color:#fff;font-size:11px;` +
      `font-weight:600;padding:3px 8px;border-radius:10px;white-space:nowrap">${dist}</span>`;
  }
  html += `</div>`;

  if (body) {
    html +=
      `<p style="color:#555;font-size:13px;line-height:1.4;margin:8px 0 0;` +
      `display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden">` +
      `${body}</p>`;
  }

  if (hasActions) {
    html += `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:12px">`;
    if (directionsUrl)
      html += `<a href="${directionsUrl}" target="_blank" rel="noopener" style="${btnStyle}">Get Directions</a>`;
    if (websiteUrl)
      html += `<a href="${websiteUrl}" target="_blank" rel="noopener" style="${btnStyle}">Visit Website</a>`;
    html += `</div>`;
  }

  html += `</div>`;
  return html;
}

// Muted road-map style keeps focus on our markers
const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

// ── Component ───────────────────────────────────────────────────────────

interface Props {
  height?: string;
  showCategories?: boolean;
  /** When provided, used instead of internal fetch. Allows parent to pass pre-fetched POIs. */
  pois?: NeighborhoodPOI[];
  /** When provided, uses useCollectionBinding at this path for editor overrides. Otherwise uses raw property.neighborhood_pois. */
  collectionBindingPath?: string;
}

export function NeighborhoodMapEmbed({ height = '500px', showCategories = true, pois, collectionBindingPath }: Props) {
  const property = useProperty();
  const bindingPath = collectionBindingPath ?? 'propertyneighborhood.embed_fallback';
  const bindingPois = useCollectionBinding<NeighborhoodPOI>(bindingPath, { collection: 'neighborhood_pois' });
  const allPois = pois ?? bindingPois;

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const mapElRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoRef = useRef<any>(null);

  const lat = parseCoordinate(property?.property_address_latitude);
  const lng = parseCoordinate(property?.property_address_longitude);
  const logo =
    property?.property_primary_logo || property?.property_mono_white_logo || property?.property_secondary_logo || '';

  const validPois = useMemo(
    () =>
      (allPois || []).filter(
        (p): p is NeighborhoodPOI =>
          parseCoordinate(p?.poi_latitude) !== null && parseCoordinate(p?.poi_longitude) !== null,
      ),
    [allPois],
  );

  const filtered = useMemo(
    () => (activeCategory ? validPois.filter(p => p.poi_type === activeCategory) : validPois),
    [validPois, activeCategory],
  );

  // Pre-build SVG data-URLs per category (stable across renders)
  const iconUrls = useMemo(() => {
    const m = new Map<string, string>();
    CATEGORIES.forEach(c => m.set(c.key, buildMarkerSvg(c.icon)));
    return m;
  }, []);

  // Only show categories that have at least one POI
  const visibleCategories = useMemo(() => {
    const populated = new Set(validPois.map(p => p.poi_type));
    return CATEGORIES.filter(c => populated.has(c.key));
  }, [validPois]);

  // Clear the filter if the selected category disappears.
  useEffect(() => {
    if (activeCategory === null) return;
    if (visibleCategories.some(c => c.key === activeCategory)) return;
    setActiveCategory(null);
  }, [visibleCategories, activeCategory]);

  // ── Pill bar scroll arrows ──

  const pillsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = pillsRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = pillsRef.current;
    if (!el) return;
    updateScrollArrows();
    el.addEventListener('scroll', updateScrollArrows, { passive: true });
    const ro = new ResizeObserver(updateScrollArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollArrows);
      ro.disconnect();
    };
  }, [updateScrollArrows, visibleCategories]);

  const scrollPills = (dir: 'left' | 'right') => {
    pillsRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  // ── Lazy-load via IntersectionObserver ──

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ── Initialise Google Map once visible ──

  useEffect(() => {
    if (!visible || !API_KEY || !mapElRef.current || lat === null || lng === null) return;
    let dead = false;

    loadGoogleMaps(API_KEY).then(() => {
      if (dead || !mapElRef.current) return;
      const g = (window as any).google;

      const map = new g.maps.Map(mapElRef.current, {
        center: { lat, lng },
        zoom: 14,
        styles: MAP_STYLES,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });

      infoRef.current = new g.maps.InfoWindow();
      map.addListener('click', () => infoRef.current?.close());

      // Property marker — logo on a white circle via OverlayView (avoids stretching / transparency)
      if (logo) {
        const overlay = new g.maps.OverlayView();
        const position = new g.maps.LatLng(lat, lng);
        let el: HTMLDivElement | null = null;

        overlay.onAdd = function () {
          el = document.createElement('div');
          el.style.cssText = 'position:absolute;cursor:pointer';
          el.title = property?.property_name || 'Property';
          el.innerHTML =
            '<div style="width:52px;height:52px;background:var(--case-secondary,#222);border-radius:50%;' +
            'box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;' +
            'justify-content:center;overflow:hidden;border:2px solid var(--case-secondary,#222)">' +
            `<img src="${logo}" style="width:36px;height:36px;object-fit:contain" alt="" />` +
            '</div>';
          this.getPanes()!.overlayMouseTarget.appendChild(el);
        };
        overlay.draw = function () {
          if (!el) return;
          const pt = this.getProjection().fromLatLngToDivPixel(position);
          if (!pt) return;
          el.style.left = `${pt.x - 26}px`;
          el.style.top = `${pt.y - 26}px`;
        };
        overlay.onRemove = function () {
          el?.remove();
          el = null;
        };
        overlay.setMap(map);
      } else {
        const primary =
          getComputedStyle(document.documentElement).getPropertyValue('--prop-primary').trim() || '#b47f00';
        new g.maps.Marker({
          position: { lat, lng },
          map,
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: primary,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 3,
          },
          zIndex: 1000,
          title: property?.property_name || 'Property',
        });
      }

      mapRef.current = map;
      setReady(true);
    });

    return () => {
      dead = true;
    };
  }, [visible, lat, lng, logo, property?.property_name]);

  // ── Sync POI markers whenever the filtered set changes ──

  useEffect(() => {
    if (!ready || !mapRef.current || lat === null || lng === null) return;
    const g = (window as any).google;
    const map = mapRef.current;
    const info = infoRef.current;

    // Clear previous POI markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    // Haversine distance in miles from property center
    const distMi = (pLat: number, pLng: number) => {
      const toRad = (v: number) => (v * Math.PI) / 180;
      const dLat = toRad(pLat - lat);
      const dLng = toRad(pLng - lng);
      const a =
        Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat)) * Math.cos(toRad(pLat)) * Math.sin(dLng / 2) ** 2;
      return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    // Build markers and collect distances for outlier detection
    const poiWithDist: { pos: { lat: number; lng: number }; dist: number }[] = [];

    for (const poi of filtered) {
      const pLat = parseCoordinate(poi.poi_latitude);
      const pLng = parseCoordinate(poi.poi_longitude);
      if (pLat === null || pLng === null) continue;
      const pos = { lat: pLat, lng: pLng };

      const url = iconUrls.get(poi.poi_type || '');
      const marker = new g.maps.Marker({
        position: pos,
        map,
        icon: url ? { url, scaledSize: new g.maps.Size(32, 32), anchor: new g.maps.Point(16, 16) } : undefined,
        title: poi.poi_name || '',
        zIndex: 100,
      });

      if (poi.poi_name) {
        marker.addListener('click', () => {
          info.setContent(buildInfoContent(poi));
          info.open(map, marker);
        });
      }

      markersRef.current.push(marker);
      poiWithDist.push({ pos, dist: distMi(pLat, pLng) });
    }

    if (poiWithDist.length > 0) {
      // Fit bounds to the 90th-percentile cluster so outliers don't dominate zoom
      const sorted = poiWithDist.map(p => p.dist).sort((a, b) => a - b);
      const p90 = sorted[Math.floor(sorted.length * 0.9)] ?? sorted[sorted.length - 1];
      const cutoff = Math.max(p90 * 1.2, 1); // 20% buffer, at least 1 mile

      const bounds = new g.maps.LatLngBounds();
      bounds.extend({ lat, lng });
      for (const p of poiWithDist) {
        if (p.dist <= cutoff) bounds.extend(p.pos);
      }

      map.fitBounds(bounds, 0);
      g.maps.event.addListenerOnce(map, 'idle', () => {
        const z = map.getZoom() ?? 14;
        if (z > 14) map.setZoom(14);
      });
    }
  }, [filtered, ready, iconUrls, lat, lng]);

  // ── Render ──

  if (!API_KEY) {
    return (
      <div className="flex items-center justify-center py-16" style={{ background: 'var(--case-gallery,#f0f0f0)' }}>
        <p className="text-sm" style={{ color: 'var(--case-text-muted,#757575)' }}>
          Google Maps API key not configured.
        </p>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="flex flex-col w-full">
      {/* Map canvas */}
      <div className="relative w-full" style={{ height }}>
        <div ref={mapElRef} className="w-full h-full" />
        {!ready && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            style={{ background: 'var(--case-gallery,#f0f0f0)' }}
          >
            <span className="text-sm animate-pulse" style={{ color: 'var(--case-text-muted,#6b6b6b)' }}>
              Loading map…
            </span>
          </div>
        )}
      </div>

      {/* Category pill bar */}
      {showCategories && visibleCategories.length > 0 && (
        <>
          <div className="relative flex items-stretch" style={{ backgroundColor: 'var(--prop-primary,#b47f00)' }}>
            {/* Left scroll arrow */}
            {canScrollLeft && (
              <button
                onClick={() => scrollPills('left')}
                className="absolute left-0 top-0 bottom-0 z-10 flex items-center px-3 text-white/90 hover:text-white"
                style={{ background: 'linear-gradient(to right, var(--prop-primary,#b47f00) 60%, transparent)' }}
                aria-label="Scroll categories left"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
            )}

            <div
              ref={pillsRef}
              className="nb-map-categories flex overflow-x-auto w-full"
              style={{ scrollbarWidth: 'none' }}
            >
              {visibleCategories.map((cat, idx) => {
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(current => current === cat.key ? null : cat.key)}
                    className={`shrink-0 md:shrink md:flex-1 flex items-center justify-center gap-2
                      px-5 py-3 text-xs font-medium tracking-wide uppercase whitespace-nowrap
                      transition-all duration-200
                      ${isActive ? 'text-white' : 'text-white/80 hover:text-white'}`}
                    aria-pressed={isActive}
                    style={{
                      backgroundColor: 'var(--prop-primary,#b47f00)',
                      backgroundImage: isActive ? 'linear-gradient(rgba(0,0,0,0.25),rgba(0,0,0,0.25))' : 'none',
                      fontFamily: 'var(--vibe-font-body,sans-serif)',
                      borderRight: idx < visibleCategories.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
                    }}
                  >
                    <FontAwesomeIcon icon={cat.icon} className="text-[0.65rem]" />
                    <span className="hidden sm:inline">{cat.label}</span>
                    <span className="sm:hidden">{cat.shortLabel}</span>
                  </button>
                );
              })}
            </div>

            {/* Right scroll arrow */}
            {canScrollRight && (
              <button
                onClick={() => scrollPills('right')}
                className="absolute right-0 top-0 bottom-0 z-10 flex items-center px-3 text-white/90 hover:text-white"
                style={{ background: 'linear-gradient(to left, var(--prop-primary,#b47f00) 60%, transparent)' }}
                aria-label="Scroll categories right"
              >
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            )}
          </div>
          <style>{`.nb-map-categories::-webkit-scrollbar{display:none}`}</style>
        </>
      )}
    </div>
  );
}

export default NeighborhoodMapEmbed;
